import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DateInput } from '@components';
import {
  appContext,
  filterToSelection,
  NO_ORGANIZATION,
  pickerDayOrSkip,
  selectionToFilter,
  SYSTEM_ADMINISTRATOR,
  TIME_PERIOD_OPTIONS,
  TimePeriod,
} from '@utils';

interface Option {
  id: string;
  label: string;
}

const SEARCH_DEBOUNCE_MS = 400;

/** Same set of ids, regardless of order. */
const sameIds = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((id, index) => id === sortedB[index]);
};

function MultiSelect({
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  allLabel: string;
  options: Option[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  // Local, instantly-updated selection; committed to the store (and its network fetch) after a short
  // debounce so toggling several options in a row coalesces into one request, like the search box.
  const [pendingValue, setPendingValue] = useState(value);

  useEffect(() => {
    setPendingValue((current) => (sameIds(current, value) ? current : value));
  }, [value]);

  useEffect(() => {
    if (sameIds(pendingValue, value)) return undefined;
    const timer = setTimeout(() => onChange(pendingValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Only the local selection should (re)start the debounce timer; `value`/`onChange` changing on their
    // own (e.g. a store reset) must not re-fire a commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingValue]);

  const selected = useMemo(
    () => options.filter((option) => pendingValue.includes(option.id)),
    [options, pendingValue],
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      sx={{ minWidth: 220, maxWidth: 320 }}
      options={options}
      value={selected}
      limitTags={1}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(option) => option.label}
      onChange={(_, next) => setPendingValue(next.map((option) => option.id))}
      renderOption={(props, option, { selected: isSelected }) => (
        <li {...props} key={option.id}>
          <Checkbox size="small" checked={isSelected} sx={{ mr: 1 }} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={pendingValue.length ? '' : allLabel} InputLabelProps={{ shrink: true }} />
      )}
    />
  );
}

/** Shared wiring for an id-based multi-select backed by `{ ids, includeNone }` filter fields. */
function useIdFilter<T extends { id: string; deleted?: boolean | null }>(
  items: T[],
  noneKey: string,
  noneLabel: string,
  labelOf: (item: T) => string,
  ids: string[] | null,
  includeNone: boolean,
  onApply: (next: { ids: string[] | null; includeNone: boolean }) => void,
) {
  const options = useMemo<Option[]>(
    () => [{ id: noneKey, label: noneLabel }, ...items.map((item) => ({ id: item.id, label: `${labelOf(item)}${item.deleted ? ' (deleted)' : ''}` }))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, noneKey, noneLabel],
  );
  const value = useMemo(() => filterToSelection(ids, includeNone, noneKey), [ids, includeNone, noneKey]);
  const onChange = (selected: string[]) => onApply(selectionToFilter(selected, noneKey));

  return { options, value, onChange };
}

export const ActivityLogFilters = observer(() => {
  const { activityLogStore } = useContext(appContext);
  const { filters, options, total, isExporting, canQuery, setFilters, exportCsv } = activityLogStore;
  const [search, setSearch] = useState(filters.search);

  useEffect(() => {
    if (search === filters.search) return undefined;
    const timer = setTimeout(() => setFilters({ search }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, filters.search, setFilters]);

  const organizationFilter = useIdFilter(
    options.organizations,
    NO_ORGANIZATION,
    'No organization',
    (org) => org.name ?? org.id,
    filters.orgIds,
    filters.includeNoOrganization,
    ({ ids: orgIds, includeNone }) => setFilters({ orgIds, includeNoOrganization: includeNone }),
  );
  const administratorFilter = useIdFilter(
    options.administrators,
    SYSTEM_ADMINISTRATOR,
    'System',
    (admin) => admin.name ?? admin.email ?? admin.id,
    filters.superadminIds,
    filters.includeSystem,
    ({ ids: superadminIds, includeNone }) => setFilters({ superadminIds, includeSystem: includeNone }),
  );
  const actionOptions: Option[] = useMemo(
    () => options.actions.map((action) => ({
      id: action.name,
      label: action.label ? `${action.label} (${action.name})` : action.name,
    })),
    [options.actions],
  );

  // Ignore invalid (partially typed) dates so the picker keeps what the user is typing; commit only a valid day or a clear.
  const setDay = (field: 'customStart' | 'customEnd', date: Dayjs | null) => {
    const day = pickerDayOrSkip(date);
    if (day === undefined) return;
    setFilters({ [field]: day });
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
      <MultiSelect label="Organization" allLabel="All organizations" {...organizationFilter} />
      <MultiSelect label="Administrator" allLabel="All administrators" {...administratorFilter} />
      <MultiSelect
        label="Action"
        allLabel="All actions"
        options={actionOptions}
        value={filters.actions}
        onChange={(actions) => setFilters({ actions })}
      />
      <TextField
        select
        size="small"
        label="Time Period"
        sx={{ minWidth: 160 }}
        value={filters.timePeriod}
        onChange={(event) => setFilters({ timePeriod: event.target.value as TimePeriod })}
      >
        {TIME_PERIOD_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {filters.timePeriod === 'custom' && (
        <>
          <DateInput
            name="customStart"
            label="Start date (UTC)"
            value={filters.customStart ? dayjs(filters.customStart) : null}
            setFieldValue={(_, date) => setDay('customStart', date)}
          />
          <DateInput
            name="customEnd"
            label="End date (UTC)"
            value={filters.customEnd ? dayjs(filters.customEnd) : null}
            minDate={filters.customStart ? dayjs(filters.customStart) : undefined}
            error={!canQuery && filters.customStart && filters.customEnd ? 'End date is before start date' : undefined}
            setFieldValue={(_, date) => setDay('customEnd', date)}
          />
          {!canQuery && !(filters.customStart && filters.customEnd) && (
            <Typography variant="caption" color="text.secondary">
              Choose a start and end date (UTC).
            </Typography>
          )}
        </>
      )}
      <TextField
        size="small"
        label="Search"
        placeholder="Administrator, organization or action"
        sx={{ minWidth: 260 }}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {total.toLocaleString('en-US')} {total === 1 ? 'entry' : 'entries'}
      </Typography>
      <Button
        variant="contained"
        onClick={() => exportCsv()}
        disabled={!canQuery || isExporting}
        startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Export CSV
      </Button>
    </Box>
  );
});
